import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  updateDoc,
  type DocumentData,
  type QueryDocumentSnapshot,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import {
  AvailabilityBaseSchema,
  AvailabilityCreateInput,
  AvailabilityCreateSchema,
  AvailabilityDocument,
  AvailabilityUpdateInput,
  AvailabilityUpdateSchema,
} from '../models';

const AVAILABILITIES_COLLECTION = 'collaborator_availabilities';

function getCollectionRef() {
  return collection(db, AVAILABILITIES_COLLECTION);
}

export function mapAvailabilityDocument(
  document: QueryDocumentSnapshot<DocumentData>
): AvailabilityDocument | null {
  const parsed = AvailabilityBaseSchema.safeParse(document.data());
  if (!parsed.success) {
    console.warn('[availabilityService] Documento inválido ignorado', parsed.error);
    return null;
  }

  return {
    id: document.id,
    ...parsed.data,
  } satisfies AvailabilityDocument;
}

function getDocRef(id: string) {
  return doc(db, AVAILABILITIES_COLLECTION, id);
}

export async function listAvailabilities(): Promise<AvailabilityDocument[]> {
  const snap = await getDocs(getCollectionRef());

  return snap.docs
    .map((document) => mapAvailabilityDocument(document))
    .filter(Boolean) as AvailabilityDocument[];
}

export async function getAvailabilityById(id: string): Promise<AvailabilityDocument | null> {
  const snapshot = await getDoc(getDocRef(id));
  if (!snapshot.exists()) {
    return null;
  }

  const parsed = AvailabilityBaseSchema.safeParse(snapshot.data());
  if (!parsed.success) {
    console.error('[availabilityService] Falha ao parsear documento', parsed.error);
    throw new Error('Documento de disponibilidade inválido.');
  }

  return {
    id: snapshot.id,
    ...parsed.data,
  };
}

export async function createAvailability(input: AvailabilityCreateInput): Promise<string> {
  const now = Timestamp.now();
  const payload = {
    ...input,
    createdAt: input.createdAt ?? now,
    updatedAt: input.updatedAt ?? now,
  };

  const parsed = AvailabilityCreateSchema.safeParse(payload);
  if (!parsed.success) {
    throw parsed.error;
  }

  const docRef = await addDoc(getCollectionRef(), parsed.data);
  return docRef.id;
}

export function subscribeToAvailabilities(
  onChange: (availabilities: AvailabilityDocument[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const availabilitiesQuery = query(getCollectionRef(), orderBy('createdAt', 'desc'));

  return onSnapshot(
    availabilitiesQuery,
    (snapshot) => {
      const availabilities = snapshot.docs
        .map((document) => mapAvailabilityDocument(document))
        .filter(Boolean) as AvailabilityDocument[];
      onChange(availabilities);
    },
    (error) => {
      console.error('[availabilityService] Erro ao escutar disponibilidades', error);
      onError?.(error);
    }
  );
}

export async function updateAvailability(input: AvailabilityUpdateInput): Promise<void> {
  const parsed = AvailabilityUpdateSchema.safeParse({
    ...input,
    updatedAt: Timestamp.now(),
  });

  if (!parsed.success) {
    throw parsed.error;
  }

  const { id, ...updatePayload } = parsed.data;

  // Evita atualização do createdAt
  if ('createdAt' in updatePayload) {
    delete (updatePayload as Record<string, unknown>).createdAt;
  }

  await updateDoc(getDocRef(id), updatePayload);
}

export async function deleteAvailabilityById(id: string): Promise<void> {
  await deleteDoc(getDocRef(id));
}
